/**
 * AppICEMAP — Root orchestrator.
 * Bootstraps the application: initializes theme, mounts the app shell,
 * and starts core services (router, store, map).
 */

import L from "leaflet";
import { initTheme } from "./utils/theme.js";
import router from "./router.js";
import { state, dispatch } from "./store/index.js";
import { AppShell } from "./components/shell/AppShell.js";
import { AuthHeaderWidget } from "./components/shell/AuthHeaderWidget.js";
import { isAuthenticated, isAccessTokenExpired } from "./api/directus.js";
import { getMe, refreshSession, startAuthAutoRefresh } from "./api/auth.js";

// Fix Leaflet default icon paths (broken by bundlers)
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

// Register custom elements (side-effect imports)
import "./components/map/MapViewport.js";
import "./components/sheet/BottomSheet.js";
import "./components/shared/Toast.js";
import "./components/shared/SkeletonLoader.js";
import "./components/shared/OfflineBanner.js";
import "./components/shared/EmptyState.js";

// Phase 4 — Report Cards + Filters
import "./components/sheet/CategoryPills.js";
import "./components/sheet/SearchBar.js";
import "./components/sheet/ReportCard.js";
import "./components/sheet/ReportList.js";

// Phase 5 — Report Creation
import "./components/create/ICEPrivacyWarning.js";
import "./components/create/QuickReportSheet.js";

// Phase 6 — Report Detail + Social
import "./components/detail/ConfirmationBar.js";
import "./components/detail/CommentThread.js";
import "./components/detail/CommentInput.js";
import "./components/detail/PhotoGallery.js";
import "./components/detail/DetailSheet.js";

// Phase 7 — Auth
import "./components/auth/LoginScreen.js";
import "./components/auth/RegisterScreen.js";
import "./components/settings/ProfileScreen.js";

// Phase 9 — Stats Dashboard
import "./components/stats/StatsScreen.js";

// Phase 10 — PWA Polish
import "./components/pwa/InstallPrompt.js";
import "./components/pwa/ErrorBoundary.js";

/** @type {HTMLElement | null} */
let detailSheet = null;

/** @type {HTMLElement | null} */
let quickReportSheet = null;

/** @type {HTMLElement | null} */
let loginScreen = null;

/** @type {HTMLElement | null} */
let registerScreen = null;

/** @type {HTMLElement | null} */
let profileScreen = null;

/** @type {HTMLElement | null} */
let statsScreen = null;

/** @type {HTMLElement | null} */
let installPrompt = null;

/** @type {HTMLElement | null} */
let errorBoundary = null;

/**
 * Bootstrap the application.
 * Called after DOM is ready from main.js.
 */
export async function bootstrap() {
	// 1. Determine and apply initial theme
	initTheme();

	// 2. Restore session if available
	await _restoreSession();

	// 3. Register auth guard (before routes)
	router.beforeEach(async (route) => {
		if (route === "/create" || route === "/create") {
			if (!isAuthenticated()) {
				_showLoginScreen();
				return false;
			}
		}
		return true;
	});

	// 4. Register routes
	router.on("/", () => {
		console.log("[Router] Map home");
		_showMapView();
	});

	router.on("/create", () => {
		console.log("[Router] Create report");
		_showCreateSheet();
	});

	router.on("/detail/:id", (params) => {
		console.log("[Router] Detail:", params.id);
		_showDetailSheet(params.id);
	});

	router.on("/report/:id", (params) => {
		// Alias for /detail/:id
		console.log("[Router] Report:", params.id);
		_showDetailSheet(params.id);
	});

	router.on("/login", () => {
		console.log("[Router] Login");
		_showLoginScreen();
	});

	router.on("/register", () => {
		console.log("[Router] Register");
		_showRegisterScreen();
	});

	router.on("/profile", () => {
		console.log("[Router] Profile");
		_showProfileScreen();
	});

	router.on("/stats", () => {
		console.log("[Router] Stats");
		_showStatsScreen();
	});

	// 5. Mount the app shell FIRST (it calls appRoot.innerHTML = "" — doing this
	//    before the router runs avoids wiping any overlay the initial route
	//    creates, e.g. when the app reloads on #/login or #/create).
	await AppShell.mount();

	// 5b. Inject auth header widget (does not modify AppShell DOM)
	AuthHeaderWidget.init();

	// 5c. Mount PWA components (install prompt + error boundary)
	_mountPwaComponents();

	// 6. Start router (triggers initial route) — overlays now persist
	router.start();

	// 7. Init push notifications (delayed, after app is ready)
	_initPushNotifications();

	console.log("[AppICEMAP] Bootstrap complete — map + UI shell ready");
}

/* ── Session Restoration ─────────────────────────────────────────────────── */

async function _restoreSession() {
	if (!isAuthenticated()) return;

	try {
		// Access token is short-lived — refresh it up front if it's expired.
		if (isAccessTokenExpired()) {
			await refreshSession();
		}

		let user = await getMe();

		// getMe returns null on failure (e.g. token just expired) — try a
		// refresh once, then retry, before giving up.
		if (!user) {
			const ok = await refreshSession();
			if (ok) user = await getMe();
		}

		if (user) {
			dispatch("SET_USER", {
				id: user.id,
				email: user.email,
				first_name: user.first_name,
				last_name: user.last_name,
				avatar: user.avatar,
				karma: user.karma ?? 0,
				strikes: user.strikes ?? 0,
				rehabilitations: user.rehabilitations ?? 0,
				is_limited: user.is_limited ?? false,
				// Needed by utils/permissions.js — without these the admin
				// check can never see anything and the delete actions stay hidden
				role: user.role ?? null,
				policies: user.policies ?? null,
				admin_access: user.admin_access ?? undefined,
			});
			console.log("[App] Session restored for:", user.email);
		}
		// If we still have no user we DON'T clear the session here: it may be a
		// transient network issue. refreshSession() already clears the session
		// when the refresh token is genuinely rejected (401/403).
	} catch (err) {
		// Never log the user out on an unexpected/transient error.
		console.warn("[App] Session restore deferred:", err);
	}

	// Keep the access token fresh for the rest of the session.
	if (isAuthenticated()) startAuthAutoRefresh();
}

/* ── View Management ─────────────────────────────────────────────────────── */

function _showMapView() {
	_hideAllOverlays();
}

function _showCreateSheet() {
	_hideAllOverlaysExcept("create");

	if (!quickReportSheet || !document.body.contains(quickReportSheet)) {
		quickReportSheet = document.querySelector("quick-report-sheet");
	}

	if (!quickReportSheet) {
		quickReportSheet = document.createElement("quick-report-sheet");
		document.getElementById("app")?.appendChild(quickReportSheet);

		quickReportSheet.addEventListener("quick-report:close", () => {
			router.navigate("/", { replace: true });
		});

		quickReportSheet.addEventListener("quick-report:submit", () => {
			import("./api/poller.js").then(({ forceRefresh }) => {
				forceRefresh();
			});
		});
	}

	quickReportSheet.open = true;
}

function _showDetailSheet(reportId) {
	_hideAllOverlaysExcept("detail");

	if (!detailSheet || !document.body.contains(detailSheet)) {
		detailSheet = document.querySelector("detail-sheet");
	}

	if (!detailSheet) {
		detailSheet = document.createElement("detail-sheet");
		document.getElementById("app")?.appendChild(detailSheet);

		detailSheet.addEventListener("detail-sheet:close", () => {
			router.navigate("/", { replace: true });
			dispatch("SELECT_REPORT", null);
		});

		detailSheet.addEventListener("detail-sheet:vote", () => {
			import("./api/poller.js").then(({ forceRefresh }) => {
				forceRefresh();
			});
		});
	}

	const reports = state.reports || [];
	const report = reports.find((r) => r.id === reportId);

	if (report) {
		detailSheet.report = report;
		detailSheet.open = true;
		dispatch("SELECT_REPORT", reportId);
	} else {
		import("./api/reports.js").then(({ fetchReportById }) => {
			fetchReportById(reportId)
				.then((report) => {
					if (detailSheet) {
						detailSheet.report = report;
						detailSheet.open = true;
						dispatch("SELECT_REPORT", reportId);
					}
				})
				.catch(() => {
					console.warn("[App] Report not found:", reportId);
					router.navigate("/", { replace: true });
				});
		});
	}
}

function _showLoginScreen() {
	_hideAllOverlaysExcept("login");

	if (!loginScreen || !document.body.contains(loginScreen)) {
		loginScreen = document.querySelector("login-screen");
	}

	if (!loginScreen) {
		loginScreen = document.createElement("login-screen");
		document.getElementById("app")?.appendChild(loginScreen);

		loginScreen.addEventListener("auth:success", () => {
			startAuthAutoRefresh();
			router.navigate("/", { replace: true });
		});

		loginScreen.addEventListener("auth:close", () => {
			router.navigate("/", { replace: true });
		});
	}

	loginScreen.show();
}

function _showRegisterScreen() {
	_hideAllOverlaysExcept("register");

	if (!registerScreen || !document.body.contains(registerScreen)) {
		registerScreen = document.querySelector("register-screen");
	}

	if (!registerScreen) {
		registerScreen = document.createElement("register-screen");
		document.getElementById("app")?.appendChild(registerScreen);

		registerScreen.addEventListener("auth:success", () => {
			startAuthAutoRefresh();
			router.navigate("/", { replace: true });
		});

		registerScreen.addEventListener("auth:close", () => {
			router.navigate("/", { replace: true });
		});
	}

	registerScreen.show();
}

function _showProfileScreen() {
	_hideAllOverlaysExcept("profile");

	if (!profileScreen || !document.body.contains(profileScreen)) {
		profileScreen = document.querySelector("profile-screen");
	}

	if (!profileScreen) {
		profileScreen = document.createElement("profile-screen");
		document.getElementById("app")?.appendChild(profileScreen);

		profileScreen.addEventListener("profile:close", () => {
			router.navigate("/", { replace: true });
		});

		profileScreen.addEventListener("profile:logout", () => {
			router.navigate("/", { replace: true });
		});
	}

	profileScreen.show();
}

/* ── Close helpers ───────────────────────────────────────────────────────── */

function _hideAllOverlays() {
	_hideCreateSheet();
	_hideDetailSheet();
	_hideLoginScreen();
	_hideRegisterScreen();
	_hideProfileScreen();
	_hideStatsScreen();
}

function _hideAllOverlaysExcept(keep) {
	if (keep !== "create") _hideCreateSheet();
	if (keep !== "detail") _hideDetailSheet();
	if (keep !== "login") _hideLoginScreen();
	if (keep !== "register") _hideRegisterScreen();
	if (keep !== "profile") _hideProfileScreen();
	if (keep !== "stats") _hideStatsScreen();
}

function _hideCreateSheet() {
	if (quickReportSheet) quickReportSheet.open = false;
}

function _hideDetailSheet() {
	if (detailSheet) detailSheet.open = false;
}

function _hideLoginScreen() {
	if (loginScreen) loginScreen.hide();
}

function _hideRegisterScreen() {
	if (registerScreen) registerScreen.hide();
}

function _hideProfileScreen() {
	if (profileScreen) profileScreen.hide();
}

function _showStatsScreen() {
	_hideAllOverlaysExcept("stats");

	if (!statsScreen || !document.body.contains(statsScreen)) {
		statsScreen = document.querySelector("stats-screen");
	}

	if (!statsScreen) {
		statsScreen = document.createElement("stats-screen");
		document.getElementById("app")?.appendChild(statsScreen);

		statsScreen.addEventListener("stats:close", () => {
			router.navigate("/", { replace: true });
		});
	}

	statsScreen.show();
}

function _hideStatsScreen() {
	if (statsScreen) statsScreen.hide();
}

function _mountPwaComponents() {
	// Install prompt
	if (!document.querySelector("install-prompt")) {
		installPrompt = document.createElement("install-prompt");
		document.getElementById("app")?.appendChild(installPrompt);
	}

	// Error boundary
	if (!document.querySelector("error-boundary")) {
		errorBoundary = document.createElement("error-boundary");
		document.getElementById("app")?.appendChild(errorBoundary);
	}
}

/* ── Push Notifications ──────────────────────────────────────────────────── */

async function _initPushNotifications() {
	// Delayed init to not block app startup
	setTimeout(async () => {
		try {
			const { isPushSupported, isPushEnabled, subscribeToPush } = await import(
				"./services/push-notifications.js"
			);

			if (!isPushSupported()) {
				console.log("[Push] Not supported in this browser");
				return;
			}

			// Auto-subscribe if user previously enabled
			if (isPushEnabled() && isAuthenticated()) {
				await subscribeToPush();
				console.log("[Push] Auto-subscribed");
			}
		} catch (err) {
			console.warn("[Push] Init error:", err);
		}
	}, 3000);
}
